import React, { useState, useEffect } from 'react';
import { movieApi, Movie, handleApiError, isRateLimitError, getRateLimitFromError, RateLimit, User } from '../services/api';
import MovieDetails from './MovieDetails';
import MovieCard from './MovieCard';

interface WeightSliders {
  content: number;
  script: number;
  video: number;
  audio: number;
}

const DEFAULT_WEIGHTS: WeightSliders = { content: 40, script: 20, video: 25, audio: 15 };

interface RecommendationsProps {
  movieId: number;
  movieTitle: string;
  topK?: number;
  className?: string;
  onMovieSelect?: (movie: Movie) => void;
  useHybrid?: boolean;
}

const Recommendations: React.FC<RecommendationsProps> = ({
  movieId,
  movieTitle,
  topK = 5,
  className = "",
  onMovieSelect,
  useHybrid = false
}) => {
  const [recommendations, setRecommendations] = useState<Movie[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedMovie, setSelectedMovie] = useState<Movie | null>(null);
  const [recommendationMethod, setRecommendationMethod] = useState<string>('');
  const [rateLimit, setRateLimit] = useState<RateLimit | null>(null);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [responseUser, setResponseUser] = useState<User | null>(null);
  const [sliders, setSliders] = useState<WeightSliders>(DEFAULT_WEIGHTS);
  const [showWeightPanel, setShowWeightPanel] = useState(false);

  const slidersTotal = sliders.content + sliders.script + sliders.video + sliders.audio;
  const slidersValid = slidersTotal === 100;

  useEffect(() => {
    setSliders(DEFAULT_WEIGHTS);
    fetchRecommendations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movieId, topK, useHybrid]);

  // Fetch recommendations, optionally with explicit weights.
  // When called without weights the backend uses the authenticated user's stored
  // profile defaults (or system defaults for anonymous users).
  const fetchRecommendations = async (customWeights?: WeightSliders) => {
    setIsLoading(true);
    setError('');
    setSelectedMovie(null);
    setIsRateLimited(false);

    try {
      const weightParams = (useHybrid && customWeights)
        ? {
            content: customWeights.content / 100,
            script: customWeights.script / 100,
            video: customWeights.video / 100,
            audio: customWeights.audio / 100,
          }
        : undefined;

      const response = useHybrid
        ? await movieApi.getHybridRecommendations(movieId, topK, weightParams)
        : await movieApi.getRecommendations(movieId, topK);

      setRecommendations(response.recommendations);
      setRecommendationMethod(response.method || 'word2vec');
      setRateLimit(response.rate_limit || null);
      setResponseUser(response.user || null);

      // Seed sliders from the weights the backend actually used (reflects user's profile defaults)
      const firstWeights = response.recommendations[0]?.explanation?.weights;
      if (firstWeights && response.method === 'hybrid') {
        setSliders({
          content: Math.round((firstWeights.content ?? 0.40) * 100),
          script: Math.round((firstWeights.script ?? 0.20) * 100),
          video: Math.round((firstWeights.video ?? 0.25) * 100),
          audio: Math.round((firstWeights.audio ?? 0.15) * 100),
        });
      }
    } catch (err) {
      if (isRateLimitError(err)) {
        setIsRateLimited(true);
        setRateLimit(getRateLimitFromError(err));
      }
      setError(handleApiError(err));
      setRecommendations([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplyWeights = () => {
    if (!slidersValid) return;
    fetchRecommendations(sliders);
  };

  const handleResetWeights = () => {
    // Re-fetch without explicit weights so the backend re-applies profile defaults
    fetchRecommendations();
  };

  const handleMovieClick = (movie: Movie) => {
    setSelectedMovie(movie);
    if (onMovieSelect) {
      onMovieSelect(movie);
    }
  };

  if (isLoading) {
    return (
      <div className={`${className}`}>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Finding similar movies...</p>
        </div>
      </div>
    );
  }

  if (error) {
    if (isRateLimited && rateLimit) {
      const resetTime = new Date(rateLimit.reset_time);
      const timeUntilReset = Math.max(0, Math.ceil((resetTime.getTime() - Date.now()) / (1000 * 60 * 60))); // hours

      return (
        <div className={`${className}`}>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
            <svg className="w-12 h-12 text-amber-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-lg font-semibold text-amber-800 mb-2">Daily Recommendation Limit Reached</h3>
            <p className="text-amber-700 mb-4">
              You've used all {rateLimit.daily_limit} daily recommendations for anonymous users.
            </p>
            <div className="bg-amber-100 rounded-lg p-4 mb-4">
              <p className="text-sm text-amber-800 mb-2">
                <strong>Resets in:</strong> {timeUntilReset} hour{timeUntilReset !== 1 ? 's' : ''}
              </p>
              <p className="text-sm text-amber-700">
                Want unlimited recommendations? Create an account to get unlimited access!
              </p>
            </div>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
              >
                Refresh Page
              </button>
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                onClick={() => alert('User registration feature coming soon!')}
              >
                Create Account
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={`${className}`}>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <svg className="w-12 h-12 text-red-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <h3 className="text-lg font-semibold text-red-800 mb-2">Error Loading Recommendations</h3>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => fetchRecommendations()}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (recommendations.length === 0) {
    return (
      <div className={`${className}`}>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
          <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m0 0V3a1 1 0 011 1v7.586l2.707 2.707a1 1 0 01.293.707V19a2 2 0 01-2 2H5a2 2 0 01-2-2v-4a1 1 0 01.293-.707L6 11.586V4a1 1 0 011-1z" />
          </svg>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">No Recommendations Found</h3>
          <p className="text-gray-600">We couldn't find similar movies for "{movieTitle}".</p>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Movies Similar to "{movieTitle}"
        </h2>
        <p className="text-gray-600">
          {recommendationMethod === 'hybrid'
            ? 'Based on hybrid analysis combining content features and script similarity'
            : 'Based on script similarity analysis using Word2Vec embeddings'
          }
          {recommendationMethod === 'hybrid' && (
            <span className="ml-2 px-2 py-1 bg-amber-100 text-amber-800 text-xs font-semibold rounded-full">
              🧠 Hybrid AI
            </span>
          )}
        </p>

        {/* Rate Limit Status */}
        {rateLimit && (
          <div className={`mt-3 p-3 border rounded-lg ${
            responseUser
              ? 'bg-green-50 border-green-200'
              : 'bg-blue-50 border-blue-200'
          }`}>
            <div className="flex items-center justify-between text-sm">
              <div className={`flex items-center ${
                responseUser ? 'text-green-700' : 'text-blue-700'
              }`}>
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {responseUser ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  )}
                </svg>
                <span>
                  {responseUser
                    ? `Authenticated as ${responseUser.username}: Unlimited recommendations`
                    : `Anonymous user: ${rateLimit.remaining} recommendations remaining today`
                  }
                </span>
              </div>
              {!responseUser && (
                <button
                  className="text-blue-600 hover:text-blue-800 font-medium"
                  onClick={() => alert('User registration feature coming soon!')}
                >
                  Get unlimited →
                </button>
              )}
            </div>
          </div>
        )}

        {/* Weight adjustment panel (hybrid mode only) */}
        {useHybrid && recommendationMethod === 'hybrid' && (
          <div className="mt-4 border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setShowWeightPanel(prev => !prev)}
              className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-sm font-medium text-gray-700"
            >
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
                Adjust Recommendation Weights
              </span>
              <svg
                className={`w-4 h-4 text-gray-500 transition-transform ${showWeightPanel ? 'rotate-180' : ''}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showWeightPanel && (
              <div className="px-4 py-4 bg-white space-y-4">
                {(
                  [
                    { key: 'content', label: 'Content Features', color: 'accent-blue-600' },
                    { key: 'script', label: 'Script Similarity', color: 'accent-green-600' },
                    { key: 'video', label: 'Visual Style', color: 'accent-purple-600' },
                    { key: 'audio', label: 'Audio Mood', color: 'accent-orange-500' },
                  ] as { key: keyof WeightSliders; label: string; color: string }[]
                ).map(({ key, label, color }) => (
                  <div key={key}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">{label}</span>
                      <span className="text-sm font-semibold text-gray-900 w-10 text-right">
                        {sliders[key]}%
                      </span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="1"
                      value={sliders[key]}
                      onChange={(e) =>
                        setSliders(prev => ({ ...prev, [key]: parseInt(e.target.value) }))
                      }
                      className={`w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer ${color}`}
                    />
                  </div>
                ))}

                <div className={`flex items-center justify-between text-sm rounded-lg px-3 py-2 ${
                  slidersValid ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
                }`}>
                  <span>Total: {slidersTotal}%</span>
                  {!slidersValid && (
                    <span className="font-medium">Weights must sum to 100%</span>
                  )}
                </div>

                <div className="flex gap-2 pt-1">
                  <button
                    onClick={handleApplyWeights}
                    disabled={!slidersValid}
                    className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Apply
                  </button>
                  <button
                    onClick={handleResetWeights}
                    className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Reset to defaults
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Movie Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
        {recommendations.map((movie, index) => (
          <MovieCard
            key={movie.id}
            movie={movie}
            onClick={() => handleMovieClick(movie)}
            showSimilarity={true}
            showStreaming={false}
            showExplanation={useHybrid && recommendationMethod === 'hybrid'}
            rank={index + 1}
            size="medium"
          />
        ))}
      </div>

      {/* Selected Movie Details Modal */}
      {selectedMovie && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="relative">
              <button
                onClick={() => setSelectedMovie(null)}
                className="absolute -top-4 -right-4 z-10 bg-white rounded-full p-2 shadow-lg hover:bg-gray-50 transition-colors"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <MovieDetails movie={selectedMovie} showFullDetails={true} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Recommendations;
